import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { logAudit, AUDIT_ACTIONS } from './auditService';
import { DEMO_SMILE_DESIGNS } from '../contexts/DemoContext';

const IS_DEMO = process.env.REACT_APP_DEMO_MODE === 'true';

/**
 * Smile Design Service
 * Handles CRUD operations for smile design documents stored under cases.
 * Path: practices/{practiceId}/patients/{patientId}/cases/{caseId}/smileDesigns/{designId}
 */

const designsCollection = (practiceId, patientId, caseId) =>
  collection(db, 'practices', practiceId, 'patients', patientId,
    'cases', caseId, 'smileDesigns');

const designDoc = (practiceId, patientId, caseId, designId) =>
  doc(db, 'practices', practiceId, 'patients', patientId,
    'cases', caseId, 'smileDesigns', designId);

export const saveSmileDesign = async (practiceId, userId, patientId, caseId, designData) => {
  if (IS_DEMO) {
    const id = 'demo-design-' + Date.now();
    return {
      id,
      ...designData,
      createdBy: userId,
      createdAt: new Date(),
      updatedBy: userId,
      updatedAt: new Date()
    };
  }

  try {
    const docData = {
      ...designData,
      createdBy: userId,
      createdAt: serverTimestamp(),
      updatedBy: userId,
      updatedAt: serverTimestamp()
    };
    const colRef = designsCollection(practiceId, patientId, caseId);
    const docRef = await addDoc(colRef, docData);

    await logAudit({
      practiceId, userId,
      action: AUDIT_ACTIONS.CREATE,
      resourceType: 'smileDesign',
      resourceId: docRef.id,
      details: { caseId, mode: designData.mode, name: designData.name }
    });

    return { id: docRef.id, ...docData };
  } catch (error) {
    console.error('Error saving smile design:', error);
    throw error;
  }
};

export const updateSmileDesign = async (practiceId, userId, patientId, caseId, designId, updates) => {
  if (IS_DEMO) {
    return { id: designId, ...updates, updatedBy: userId, updatedAt: new Date() };
  }

  try {
    const docRef = designDoc(practiceId, patientId, caseId, designId);
    const updateData = {
      ...updates,
      updatedBy: userId,
      updatedAt: serverTimestamp()
    };
    await updateDoc(docRef, updateData);

    await logAudit({
      practiceId, userId,
      action: AUDIT_ACTIONS.UPDATE,
      resourceType: 'smileDesign',
      resourceId: designId,
      details: { caseId }
    });

    return { id: designId, ...updateData };
  } catch (error) {
    console.error('Error updating smile design:', error);
    throw error;
  }
};

export const getSmileDesign = async (practiceId, patientId, caseId, designId) => {
  if (IS_DEMO) {
    const designs = DEMO_SMILE_DESIGNS[caseId] || [];
    const found = designs.find(d => d.id === designId);
    if (!found) throw new Error('Smile design not found');
    return { ...found };
  }

  try {
    const docRef = designDoc(practiceId, patientId, caseId, designId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) throw new Error('Smile design not found');
    return { id: snap.id, ...snap.data() };
  } catch (error) {
    console.error('Error getting smile design:', error);
    throw error;
  }
};

export const getSmileDesigns = async (practiceId, patientId, caseId) => {
  if (IS_DEMO) {
    return [...(DEMO_SMILE_DESIGNS[caseId] || [])];
  }

  try {
    const colRef = designsCollection(practiceId, patientId, caseId);
    const q = query(colRef, orderBy('updatedAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting smile designs:', error);
    throw error;
  }
};

export const deleteSmileDesign = async (practiceId, userId, patientId, caseId, designId) => {
  if (IS_DEMO) return true;

  try {
    const docRef = designDoc(practiceId, patientId, caseId, designId);
    await deleteDoc(docRef);

    await logAudit({
      practiceId, userId,
      action: AUDIT_ACTIONS.DELETE,
      resourceType: 'smileDesign',
      resourceId: designId,
      details: { caseId }
    });

    return true;
  } catch (error) {
    console.error('Error deleting smile design:', error);
    throw error;
  }
};

export default {
  saveSmileDesign,
  updateSmileDesign,
  getSmileDesign,
  getSmileDesigns,
  deleteSmileDesign
};
